import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listTasks, getAllLabels, listTaskUsers } from "../../firebase/tasks";
import { LabelTag } from "../../components/tms/LabelTag";
import TaskSearchInput from "../../components/tms/TaskSearchInput";
import { describeRecurrence, isRepeatableTask } from "../../utils/recurrenceEngine";

const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case "Critical": // keeping for legacy
    case "High":
      return "bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide inline-block";
    case "Medium":
      return "bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Low":
    default:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Open":
      return "bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "In Progress":
      return "bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "On Hold":
      return "bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Closed":
    default:
      return "bg-slate-100 text-slate-700 border border-slate-300 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return "—";
  if (timestamp.toDate && typeof timestamp.toDate === "function") {
    return timestamp.toDate().toLocaleDateString();
  }
  return new Date(timestamp).toLocaleDateString();
};

export function TasksByLabel() {
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [tasksList, labelsList, usersList] = await Promise.all([
          listTasks(),
          getAllLabels(),
          listTaskUsers()
        ]);
        setTasks(tasksList);
        setLabels(labelsList);
        setUsers(usersList);
      } catch (err) {
        console.error("Error loading tasks by label data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getAssigneeName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find((u) => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  // Group tasks by label
  const groups = labels.map((label) => {
    const matchedTasks = tasks.filter((t) => (t.labels || []).includes(label.name));
    const sorted = matchedTasks.sort((a, b) => b.taskNumber - a.taskNumber);
    return {
      labelId: label.id,
      labelName: label.name,
      color: label.color,
      tasks: sorted
    };
  });

  const activeGroups = groups.filter((g) => g.tasks.length > 0);

  const unlabeledTasks = tasks
    .filter((t) => !t.labels || t.labels.length === 0)
    .sort((a, b) => b.taskNumber - a.taskNumber);

  return (
    <div
      className="container-fluid min-h-screen py-8 px-4 md:px-8"
      style={{ backgroundColor: "var(--theme-color-canvas)" }}
    >
      {/* Header */}
      <div
        className="mb-8 pb-5 flex flex-col md:flex-row justify-between items-start md:items-center"
        style={{ borderBottom: "1px solid var(--theme-color-mute)" }}
      >
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <h1
              className="text-3xl font-extrabold tracking-tight m-0"
              style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}
            >
              Report: Tasks by Label
            </h1>
            <TaskSearchInput />
          </div>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            Review tasks grouped systematically by their applied administrative labels.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/tasks/dashboard"
            className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline"
            style={{
              backgroundColor: "var(--theme-color-canvas-soft)",
              color: "var(--theme-color-body)",
              borderColor: "var(--theme-color-mute)",
            }}
          >
            ← Admin Dashboard
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="space-y-12">
          {activeGroups.map((group) => (
            <div key={group.labelId} className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: "var(--theme-color-mute)" }}>
                <h2 className="text-xl font-bold m-0 flex items-center">
                  <LabelTag name={group.labelName} color={group.color} />
                </h2>
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {group.tasks.length} {group.tasks.length === 1 ? "task" : "tasks"}
                </span>
              </div>
              <TaskTable tasks={group.tasks} getAssigneeName={getAssigneeName} />
            </div>
          ))}

          {unlabeledTasks.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: "var(--theme-color-mute)" }}>
                <h2 className="text-xl font-bold m-0 text-gray-500">Unlabeled Tasks</h2>
                <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {unlabeledTasks.length} {unlabeledTasks.length === 1 ? "task" : "tasks"}
                </span>
              </div>
              <TaskTable tasks={unlabeledTasks} getAssigneeName={getAssigneeName} />
            </div>
          )}

          {activeGroups.length === 0 && unlabeledTasks.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No tasks found in the system.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskTable({ tasks, getAssigneeName }) {
  return (
    <div
      className="rounded-2xl border shadow-sm overflow-hidden"
      style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr
              className="text-xs font-bold uppercase tracking-wider"
              style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body-mid)", borderBottom: "1px solid var(--theme-color-mute)" }}
            >
              <th className="p-4 pl-6 w-28"># Task</th>
              <th className="p-4">Title</th>
              <th className="p-4 w-32">Status</th>
              <th className="p-4 w-32">Priority</th>
              <th className="p-4 w-36">Repeat</th>
              <th className="p-4">Assignee</th>
              <th className="p-4 w-40">Due Date</th>
              <th className="p-4 pr-6 w-40">Created</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr
                key={task.id}
                className="transition-colors"
                style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}
              >
                <td className="p-4 pl-6 align-middle font-extrabold">
                  <Link
                    to={`/tasks/${task.taskNumber}`}
                    className="hover:underline no-underline"
                    style={{ color: "var(--theme-color-primary)" }}
                  >
                    {task.taskNumber}
                  </Link>
                </td>
                <td className="p-4 align-middle font-semibold max-w-sm truncate" style={{ color: "var(--theme-color-ink)" }}>
                  {task.title}
                </td>
                <td className="p-4 align-middle">
                  <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                </td>
                <td className="p-4 align-middle">
                  <span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span>
                </td>
                <td className="p-4 align-middle text-xs font-medium" style={{ color: "var(--theme-color-body)" }}>
                  {isRepeatableTask(task) ? (
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        task.status === "Closed"
                          ? "text-gray-600 bg-gray-100"
                          : "text-indigo-700 bg-indigo-50"
                      }`}
                    >
                      <span>🔄</span>
                      <span>{describeRecurrence(task.recurrence)}</span>
                      {task.status === "Closed" && (
                        <span className="text-[10px] text-gray-500 font-normal ml-0.5">(Ended)</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                  {getAssigneeName(task.assigneeUid)}
                </td>
                <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                  {task.dueDate || "—"}
                </td>
                <td className="p-4 pr-6 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                  {formatFirestoreDate(task.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
